/**
 * WeatherWidget - Displays real-time weather and fishing conditions
 * Uses Open-Meteo API for 7-day forecasts
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Thermometer, Wind, Droplets, Sun, Moon, Cloud,
  Fish, ChevronDown, ChevronUp, Sunrise, Sunset,
  Gauge, Eye, CloudRain, Clock
} from "lucide-react";

// Fishing rating colors (handles both capitalized and lowercase ratings)
const getRatingColor = (rating) => {
  const r = rating?.toLowerCase();
  if (r === "excellent") return "bg-green-500";
  if (r === "good") return "bg-blue-500";
  if (r === "fair") return "bg-yellow-500";
  if (r === "poor") return "bg-red-500";
  return "bg-gray-500";
};

// Legacy rating colors for backwards compatibility
const ratingColors = {
  "Excellent": "bg-green-500",
  "excellent": "bg-green-500",
  "Good": "bg-blue-500",
  "good": "bg-blue-500",
  "Fair": "bg-yellow-500",
  "fair": "bg-yellow-500",
  "Poor": "bg-red-500",
  "poor": "bg-red-500"
};

// Check if icon is an emoji (Open-Meteo) or URL (legacy)
const isEmoji = (str) => {
  if (!str) return false;
  // Check if it's NOT a URL (doesn't start with http)
  return !str.startsWith("http");
};

// Render weather icon - handles both emoji and URL formats
const WeatherIcon = ({ icon, condition, size = "md" }) => {
  const sizeClasses = {
    sm: "w-6 h-6 text-lg",
    md: "w-10 h-10 text-2xl",
    lg: "w-16 h-16 text-4xl"
  };
  
  if (isEmoji(icon)) {
    return (
      <span className={`${sizeClasses[size]} flex items-center justify-center`} title={condition}>
        {icon}
      </span>
    );
  }
  
  return (
    <img 
      src={icon} 
      alt={condition}
      className={size === "lg" ? "w-16 h-16" : size === "md" ? "w-10 h-10" : "w-6 h-6"}
    />
  );
};

// Get Lucide icon for condition (fallback)
const getConditionIcon = (condition) => {
  const lowerCondition = condition?.toLowerCase() || "";
  if (lowerCondition.includes("sun") || lowerCondition.includes("clear")) {
    return <Sun className="w-5 h-5 text-yellow-500" />;
  } else if (lowerCondition.includes("cloud") || lowerCondition.includes("overcast")) {
    return <Cloud className="w-5 h-5 text-gray-400" />;
  } else if (lowerCondition.includes("rain") || lowerCondition.includes("drizzle")) {
    return <CloudRain className="w-5 h-5 text-blue-400" />;
  }
  return <Cloud className="w-5 h-5 text-gray-400" />;
};

export default function WeatherWidget({ lat, lon, compact = false, showForecast = true }) {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (lat && lon) {
      fetchWeather();
    }
  }, [lat, lon]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchWeather = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/weather/current`, {
        params: {
          lat,
          lon,
          include_forecast: showForecast,
          include_astronomy: true
        }
      });
      setWeather(response.data);
    } catch (err) {
      setError("Unable to load weather data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="w-full">
        <CardContent className="p-4">
          <div className="space-y-3">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-12 w-24" />
            <Skeleton className="h-4 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full">
        <CardContent className="p-4 text-center text-muted-foreground">
          <Cloud className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!weather) return null;

  const { current, astronomy, forecast, fishing_conditions } = weather;

  // Compact view for list items
  if (compact) {
    return (
      <div className="flex items-center gap-3 text-sm">
        <WeatherIcon icon={current.condition_icon} condition={current.condition} size="sm" />
        <span className="font-medium">{Math.round(current.temp_f)}°F</span>
        <span className="text-muted-foreground">{current.condition}</span>
        {fishing_conditions && (
          <Badge className={`${getRatingColor(fishing_conditions.rating)} text-white text-xs`}>
            <Fish className="w-3 h-3 mr-1" />
            {fishing_conditions.rating}
          </Badge>
        )}
      </div>
    );
  }

  // Format score display - handles both 1-10 and 0-100 scales
  const getScoreDisplay = () => {
    if (!fishing_conditions?.score) return null;
    // If score is > 10, it's the 0-100 scale
    if (fishing_conditions.score > 10) {
      return `${fishing_conditions.score}%`;
    }
    return `${fishing_conditions.score}/10`;
  };

  // Capitalize first letter of rating
  const formatRating = (rating) => {
    if (!rating) return "";
    return rating.charAt(0).toUpperCase() + rating.slice(1).toLowerCase();
  };

  return (
    <Card className="w-full" data-testid="weather-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Cloud className="w-5 h-5" />
            Weather & Fishing Conditions
          </CardTitle>
          {fishing_conditions && (
            <Badge className={`${getRatingColor(fishing_conditions.rating)} text-white`} data-testid="fishing-rating-badge">
              <Fish className="w-4 h-4 mr-1" />
              {formatRating(fishing_conditions.rating)} {getScoreDisplay() && `(${getScoreDisplay()})`}
            </Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          {weather.location_name ? `${weather.location_name}${weather.region ? `, ${weather.region}` : ''}` : '7-Day Forecast'}
        </p>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Current Weather */}
        <div className="flex items-center gap-4">
          <WeatherIcon icon={current.condition_icon} condition={current.condition} size="lg" />
          <div>
            <div className="text-3xl font-bold">{Math.round(current.temp_f)}°F</div>
            <div className="text-muted-foreground">{current.condition}</div>
            <div className="text-sm text-muted-foreground">
              Feels like {Math.round(current.feels_like_f)}°F
            </div>
          </div>
        </div>

        {/* Weather Details Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div className="flex items-center gap-2">
            <Wind className="w-4 h-4 text-muted-foreground" />
            <span>{current.wind_mph} mph {current.wind_dir}</span>
          </div>
          <div className="flex items-center gap-2">
            <Droplets className="w-4 h-4 text-muted-foreground" />
            <span>{current.humidity}% humidity</span>
          </div>
          <div className="flex items-center gap-2">
            <Gauge className="w-4 h-4 text-muted-foreground" />
            <span>{current.pressure_mb} mb</span>
          </div>
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-muted-foreground" />
            <span>{current.visibility_miles} mi</span>
          </div>
        </div>

        {/* Astronomy Data */}
        {astronomy && (
          <div className="flex flex-wrap gap-4 text-sm border-t pt-3">
            {astronomy.sunrise && (
              <div className="flex items-center gap-2">
                <Sunrise className="w-4 h-4 text-orange-400" />
                <span>{astronomy.sunrise}</span>
              </div>
            )}
            {astronomy.sunset && (
              <div className="flex items-center gap-2">
                <Sunset className="w-4 h-4 text-orange-600" />
                <span>{astronomy.sunset}</span>
              </div>
            )}
            {astronomy.moon_phase && (
              <div className="flex items-center gap-2">
                <Moon className="w-4 h-4 text-gray-400" />
                <span>{astronomy.moon_phase} {astronomy.moon_illumination ? `(${astronomy.moon_illumination}%)` : ''}</span>
              </div>
            )}
          </div>
        )}

        {/* Best Fishing Times - Compact Display */}
        {fishing_conditions?.best_times && fishing_conditions.best_times.length > 0 && (
          <div className="flex items-center gap-2 text-sm bg-accent/30 rounded-lg px-3 py-2" data-testid="best-fishing-times">
            <Clock className="w-4 h-4 text-green-600 flex-shrink-0" />
            <span className="text-muted-foreground">Best times:</span>
            <span className="font-medium truncate">{fishing_conditions.best_times.slice(0, 2).join(" • ")}</span>
          </div>
        )}

        {/* Fishing Factors */}
        {fishing_conditions && fishing_conditions.factors.length > 0 && (
          <div className="border-t pt-3">
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-2 text-sm font-medium w-full"
            >
              <Fish className="w-4 h-4" />
              Fishing Factors
              {expanded ? <ChevronUp className="w-4 h-4 ml-auto" /> : <ChevronDown className="w-4 h-4 ml-auto" />}
            </button>
            {expanded && (
              <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                {fishing_conditions.factors.map((factor, i) => (
                  <li key={`factor-${factor.slice(0, 20)}-${i}`} className="flex items-start gap-2">
                    <span className="text-accent">•</span>
                    {factor}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* 7-Day Forecast */}
        {showForecast && forecast && forecast.length > 0 && (
          <div className="border-t pt-3" data-testid="weather-forecast">
            <h4 className="text-sm font-medium mb-3">{forecast.length}-Day Forecast</h4>
            <div className="grid gap-1 text-center text-xs grid-cols-7">
              {forecast.map((day) => (
                <div key={day.full_date || day.date} className="space-y-1">
                  <div className="font-medium">
                    {day.date_display?.split(",")[0] || day.date.slice(5)}
                  </div>
                  <div className="flex justify-center">
                    <WeatherIcon 
                      icon={day.condition_icon} 
                      condition={day.condition} 
                      size="sm" 
                    />
                  </div>
                  <div className="font-medium">{Math.round(day.max_temp_f)}°</div>
                  <div className="text-muted-foreground">{Math.round(day.min_temp_f)}°</div>
                  {day.chance_of_rain > 0 && (
                    <div className="text-blue-400 flex items-center justify-center gap-0.5">
                      <Droplets className="w-3 h-3" />
                      {day.chance_of_rain}%
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Mini version for destination cards
export function WeatherBadge({ lat, lon }) {
  const [weather, setWeather] = useState(null);

  useEffect(() => {
    if (lat && lon) {
      axios.get(`${API}/weather/current`, {
        params: { lat, lon, include_forecast: false, include_astronomy: false }
      })
      .then(res => setWeather(res.data))
      .catch(() => {});
    }
  }, [lat, lon]);

  if (!weather) return null;

  return (
    <div className="flex items-center gap-1.5 text-xs bg-background/80 backdrop-blur-sm rounded-full px-2 py-1" data-testid="weather-badge">
      <WeatherIcon 
        icon={weather.current.condition_icon} 
        condition={weather.current.condition} 
        size="sm" 
      />
      <span className="font-medium">{Math.round(weather.current.temp_f)}°F</span>
      {weather.fishing_conditions && (
        <Badge 
          variant="outline" 
          className={`${getRatingColor(weather.fishing_conditions.rating)} text-white border-0 text-[10px] px-1.5 py-0`}
        >
          {weather.fishing_conditions.rating}
        </Badge>
      )}
    </div>
  );
}
